import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    editarCobro, registrarAltaCobro, quitarAltaCobro, reabrirDeudorPlanilla, darDeBajaCobro, archivarCobro, sacarCobroDeArchivo,
    anularPagoCobro, registrarGestionCobro,
} from '../../services/cobranzaService';
import { rutaDetalleTramite } from '../../utils/rutasTramite';
import InputMonto from '../InputMonto';
import { PAGO_TODO, esPagoTodo } from './resumenCobro';

const hoyIso = () => new Date().toISOString().slice(0, 10);

/** El motivo con el que Deudores da por cerrada una deuda vieja de la planilla (DeudoresPlanillaService). */
const MOTIVO_CIERRE_PLANILLA = 'Deuda vieja de la planilla: se da por cerrada y se archiva';

export const formatoFecha = (v) => (v ? new Date(`${v}T00:00:00`).toLocaleDateString('es-AR') : '—');
export const formatoFechaHora = (v) => (v ? new Date(v).toLocaleString('es-AR') : '—');
export const formatoMoneda = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }));

const TIPOS_PAGO = [
    { id: 'UN_SOLO_PAGO', label: 'Un solo pago' },
    { id: 'EN_CUOTAS', label: 'En cuotas' }
];
const CANALES = ['LLAMADA', 'WHATSAPP', 'PRESENCIAL', 'EMAIL', 'OTRO'];

/**
 * Las piezas del cobro que comparten las secciones (ver SeccionesCobro, que las arma, y
 * DetalleCobranza, que es lo que se ve en las tres pantallas): trámites enganchados, formulario del
 * pacto, alta y baja, alta de una gestión, lista de gestiones y anulación de un pago, más los formatos
 * de fecha y moneda.
 *
 * Bootstrap puro (form-control/form-select) para que se sienta parte del mismo sistema que el resto
 * de las fichas — nada de MUI TextField/Tabs acá.
 *
 * Antes acá también estaban los cuatro paneles de las pestañas (Datos, Plan de cuotas, Pagos y
 * Bitácora). Se retiraron cuando las tres pantallas pasaron a usar DetalleCobranza.
 */

// -------- Datos: pacto, montos, banco, trámites enganchados, alta/baja --------

/** Los trámites a los que está enganchado el cobro: con link a su ficha si tienen carpeta. */
export function EnganchadosCobro({ cobro }) {
    if (!(cobro.tramites?.length > 0)) return null;
    return (
        <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
            <span className="text-muted small fw-semibold">Trámites enganchados:</span>
            {cobro.tramites.map((t) => (
                t.idTramite ? (
                    <a
                        key={t.id}
                        href={rutaDetalleTramite({ idTramite: t.idTramite, nombreTipoTramite: t.tipoTramiteNombre })}
                        className="badge rounded-pill text-decoration-none"
                        style={{ background: '#e0f2fe', color: '#0369a1', padding: '0.5rem 0.9rem' }}
                    >
                        {t.tipoTramiteNombre}
                    </a>
                ) : (
                    <span key={t.id} className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#475569', padding: '0.5rem 0.9rem' }}>
                        {t.tipoTramiteNombre} (sin carpeta)
                    </span>
                )
            ))}
        </div>
    );
}

/**
 * Formulario del pacto (modalidad, haberes, porcentaje, monto, banco, fecha favorable y observaciones)
 * con su botón de guardar.
 *
 * @param mostrarSaldo  muestra "Saldo: $X" junto al botón (el panel de Datos sí; la sección de pacto
 *                      no, porque el resumen de arriba ya lo muestra)
 * @param onCancelar    si viene, agrega un botón para salir sin guardar
 */
/**
 * @param puedeConfigurar false deshabilita la configuración (tipo de pago, haberes, porcentaje, monto):
 *                        la cambia administración, salvo en un plan de pago (ver permisosCobro.js).
 *                        El resto (banco, fechas, observaciones) lo corrige cualquiera.
 */
export function FormularioPactoCobro({ cobro, onGuardado, onCancelar, mostrarSaldo = true, puedeConfigurar = true }) {
    const [form, setForm] = useState(mapCobroAForm(cobro));
    const [guardando, setGuardando] = useState(false);

    useEffect(() => { setForm(mapCobroAForm(cobro)); }, [cobro]);

    const handleGuardar = async () => {
        setGuardando(true);
        try {
            await editarCobro(cobro.idCobro, {
                tipoPago: form.tipoPago || null,
                cantidadHaberes: form.cantidadHaberes === '' ? null : Number(form.cantidadHaberes),
                conPorcentaje: form.conPorcentaje,
                porcentaje: form.porcentaje === '' ? null : Number(form.porcentaje),
                montoTotal: form.montoTotal === '' ? null : Number(form.montoTotal),
                banco: form.banco?.trim() || null,
                fechaFavorable: form.fechaFavorable || null,
                // Corregir la fecha de alta solo tiene sentido si ya hay alta; si no, ni se manda.
                ...(form.fechaAlta ? { fechaAlta: form.fechaAlta } : {}),
                observaciones: form.observaciones?.trim() || null,
            });
            toast.success('Datos guardados.');
            onGuardado?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo guardar.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <>
            {!puedeConfigurar && (
                <div className="alert alert-light border small py-2">
                    <i className="bi bi-lock me-1"></i>
                    El tipo de pago, los haberes, el porcentaje y el monto los cambia administración.
                </div>
            )}
            <div className="row g-3">
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Tipo de pago</label>
                    <select className="form-select" disabled={!puedeConfigurar} value={form.tipoPago} onChange={(e) => setForm((f) => ({ ...f, tipoPago: e.target.value }))}>
                        <option value="">Sin definir</option>
                        {TIPOS_PAGO.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                </div>
                <div className="col-md-6">
                    <label className="form-label fw-semibold" htmlFor="cobro-haberes">Cantidad de haberes</label>
                    <input id="cobro-haberes" type="number" className="form-control" disabled={!puedeConfigurar} value={form.cantidadHaberes} onChange={(e) => setForm((f) => ({ ...f, cantidadHaberes: e.target.value }))} />
                </div>
                <div className="col-md-6 d-flex align-items-center">
                    <div className="form-check mt-4">
                        <input className="form-check-input" type="checkbox" id="conPorcentaje" disabled={!puedeConfigurar} checked={form.conPorcentaje} onChange={(e) => setForm((f) => ({ ...f, conPorcentaje: e.target.checked }))} />
                        <label className="form-check-label fw-semibold" htmlFor="conPorcentaje">Con porcentaje (30%)</label>
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Porcentaje</label>
                    <div className="input-group">
                        <input type="number" className="form-control" disabled={!form.conPorcentaje || !puedeConfigurar} value={form.porcentaje} onChange={(e) => setForm((f) => ({ ...f, porcentaje: e.target.value }))} />
                        <span className="input-group-text">%</span>
                    </div>
                    <div className="form-text">
                        Adicional sobre las diferencias retroactivas que se cobren, además de los haberes pactados (el pacto "+30%"). Si se deja vacío y "Con porcentaje" está tildado, se usa 30%.
                    </div>
                </div>
                {form.tipoPago !== 'UN_SOLO_PAGO' && (
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Monto total pactado</label>
                        <InputMonto disabled={!puedeConfigurar} value={form.montoTotal} onChange={(v) => setForm((f) => ({ ...f, montoTotal: v }))} />
                    </div>
                )}
                <div className="col-md-6">
                    <label className="form-label fw-semibold" htmlFor="cobro-banco">Banco</label>
                    <input id="cobro-banco" type="text" className="form-control" value={form.banco} onChange={(e) => setForm((f) => ({ ...f, banco: e.target.value }))} />
                </div>
                <div className="col-md-6">
                    <label className="form-label fw-semibold">Fecha favorable</label>
                    <input type="date" className="form-control" value={form.fechaFavorable} onChange={(e) => setForm((f) => ({ ...f, fechaFavorable: e.target.value }))} />
                </div>
                <div className="col-md-6">
                    <label className="form-label fw-semibold" htmlFor="cobro-fecha-alta">Fecha de alta</label>
                    <input
                        id="cobro-fecha-alta"
                        type="date"
                        className="form-control"
                        value={form.fechaAlta}
                        onChange={(e) => setForm((f) => ({ ...f, fechaAlta: e.target.value }))}
                    />
                    <div className="form-text">
                        {cobro.fechaAlta
                            ? 'El día que va a cobrar (la que publicó ANSES). Se puede corregir.'
                            : 'El día que va a cobrar, cuando ANSES lo publique. Al cargarla pasa a Altas.'}
                    </div>
                </div>
                <div className="col-12">
                    <label className="form-label fw-semibold">Observaciones</label>
                    <textarea className="form-control" rows={2} value={form.observaciones} onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))} />
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
                {mostrarSaldo ? (
                    <span className="fs-5">{esPagoTodo(cobro)
                        ? <strong style={{ color: '#166534' }}>{PAGO_TODO}</strong>
                        : <>Saldo: <strong style={{ color: '#0d9488' }}>{formatoMoneda(cobro.saldo)}</strong></>}</span>
                ) : <span />}
                <div className="d-flex gap-2">
                    {onCancelar && (
                        <button className="btn btn-outline-secondary rounded-pill px-4" onClick={onCancelar} disabled={guardando}>Cancelar</button>
                    )}
                    <button className="btn btn-primary rounded-pill px-4 fw-semibold" onClick={handleGuardar} disabled={guardando}>
                        {guardando ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check2 me-2"></i>Guardar cambios</>}
                    </button>
                </div>
            </div>
        </>
    );
}

/**
 * Cargar la fecha de alta (el día que va a cobrar, que publica ANSES: no hay "alta hoy"), volver a
 * Favorables, archivo y baja, con el motivo de la baja si ya está dado de baja.
 */
export function AccionesCobro({ cobro, onCambio, puedeDarDeBaja = true }) {
    const [motivoBaja, setMotivoBaja] = useState('');
    const [mostrarBaja, setMostrarBaja] = useState(false);
    const [cargandoAlta, setCargandoAlta] = useState(false);
    const [fechaAlta, setFechaAlta] = useState('');

    const handleRegistrarAlta = async () => {
        if (!fechaAlta) {
            toast.error('Elegí el día que va a cobrar.');
            return;
        }
        try {
            await registrarAltaCobro(cobro.idCobro, fechaAlta);
            toast.success('Alta cargada: pasa a Altas.');
            setCargandoAlta(false);
            setFechaAlta('');
            onCambio?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo cargar el alta.');
        }
    };

    // Deuda vieja de la planilla dada por cerrada desde Deudores (V273): se puede reabrir.
    const handleReabrir = async () => {
        if (!window.confirm('¿Reabrir la deuda? Sale de Archivo y vuelve a Deudores.')) return;
        try {
            await reabrirDeudorPlanilla(cobro.idCobro);
            toast.success('Reabierta: volvió a Deudores.');
            onCambio?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo reabrir.');
        }
    };

    // Volver a Favorables: solo si todavía no hay pagos ni plan de cuotas (el backend lo controla).
    const puedeVolverAFavorables = cobro.estado === 'ALTA' && cobro.fechaAlta && !cobro.archivadoEn
        && !(cobro.pagos || []).length && !(cobro.cuotas || []).some((c) => !c.anulada);
    const handleVolverAFavorables = async () => {
        if (!window.confirm('¿Volver el cobro a Favorables? Se quita la fecha de alta (queda anotado en la bitácora y en el trámite).')) return;
        try {
            await quitarAltaCobro(cobro.idCobro);
            toast.success('Volvió a Favorables.');
            onCambio?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo volver a Favorables.');
        }
    };

    // Archivo (V269): con alta, aunque quede saldo en cuotas (en ese caso sigue en Deudores).
    const puedeArchivar = cobro.fechaAlta && !cobro.archivadoEn && cobro.estado !== 'BAJA';
    const handleArchivo = async (archivar) => {
        const pregunta = archivar
            ? '¿Pasar el cobro a archivo? Sale de Altas; si quedó saldo en cuotas sigue en Deudores.'
            : '¿Sacar el cobro de archivo? Vuelve a Altas y se borra su fila del Archivo (con el seguimiento del reajuste, si se cargó).';
        if (!window.confirm(pregunta)) return;
        try {
            await (archivar ? archivarCobro(cobro.idCobro) : sacarCobroDeArchivo(cobro.idCobro));
            toast.success(archivar ? 'Pasado a archivo.' : 'Sacado de archivo.');
            onCambio?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo actualizar el archivo.');
        }
    };

    const handleDarDeBaja = async () => {
        if (!motivoBaja.trim()) {
            toast.error('Ingresá un motivo para la baja.');
            return;
        }
        try {
            await darDeBajaCobro(cobro.idCobro, motivoBaja.trim());
            toast.success('Cobro dado de baja.');
            setMostrarBaja(false);
            onCambio?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo dar de baja.');
        }
    };

    return (
        <>
            <div className="d-flex gap-2 flex-wrap align-items-center">
                {cobro.estado === 'A_COBRAR' && !cargandoAlta && (
                    <button className="btn btn-outline-primary rounded-pill px-4 fw-semibold" onClick={() => setCargandoAlta(true)}>
                        <i className="bi bi-calendar-check me-2"></i>Cargar fecha de alta
                    </button>
                )}
                {cobro.estado === 'A_COBRAR' && cargandoAlta && (
                    <span className="d-inline-flex gap-2 align-items-center flex-wrap">
                        <input type="date" className="form-control" style={{ width: 'auto' }} aria-label="Día que va a cobrar"
                               value={fechaAlta} onChange={(e) => setFechaAlta(e.target.value)} />
                        <button className="btn btn-primary rounded-pill px-4 fw-semibold" onClick={handleRegistrarAlta}>Pasar a Altas</button>
                        <button className="btn btn-outline-secondary rounded-pill px-3" onClick={() => setCargandoAlta(false)}>Cancelar</button>
                    </span>
                )}
                {puedeVolverAFavorables && (
                    <button className="btn btn-outline-secondary rounded-pill px-4 fw-semibold" onClick={handleVolverAFavorables}>
                        <i className="bi bi-arrow-counterclockwise me-2"></i>Volver a Favorables
                    </button>
                )}
                {puedeArchivar && (
                    <button className="btn btn-outline-secondary rounded-pill px-4 fw-semibold" onClick={() => handleArchivo(true)}>
                        <i className="bi bi-archive me-2"></i>Pasar a archivo
                    </button>
                )}
                {cobro.archivadoEn && (
                    <>
                        <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis border px-3 py-2">
                            <i className="bi bi-archive-fill me-1"></i>
                            En archivo desde el {new Date(cobro.archivadoEn).toLocaleDateString('es-AR')}
                            {cobro.archivadoPor ? ` · ${cobro.archivadoPor}` : ''}
                        </span>
                        <button className="btn btn-link btn-sm" onClick={() => handleArchivo(false)}>Sacar de archivo</button>
                    </>
                )}
                {puedeDarDeBaja && cobro.estado !== 'BAJA' && !mostrarBaja && (
                    <button className="btn btn-outline-danger rounded-pill px-4 fw-semibold" onClick={() => setMostrarBaja(true)}>
                        <i className="bi bi-x-circle me-2"></i>Dar de baja
                    </button>
                )}
            </div>

            {cobro.estado === 'BAJA' && cobro.motivoBaja && (
                <div className="alert alert-warning border-0 rounded-3 mt-3 mb-0 d-flex flex-wrap align-items-center gap-2">
                    <span className="me-auto">Motivo de baja: {cobro.motivoBaja}</span>
                    {cobro.motivoBaja === MOTIVO_CIERRE_PLANILLA && (
                        <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={handleReabrir}>
                            <i className="bi bi-arrow-counterclockwise me-1"></i>Reabrir (vuelve a Deudores)
                        </button>
                    )}
                </div>
            )}

            {mostrarBaja && (
                <div className="d-flex gap-2 align-items-center mt-3 flex-wrap">
                    <input type="text" className="form-control" placeholder="Motivo de la baja" value={motivoBaja} onChange={(e) => setMotivoBaja(e.target.value)} style={{ flex: 1, minWidth: 220 }} />
                    <button className="btn btn-danger rounded-pill px-4 fw-semibold" onClick={handleDarDeBaja}>Confirmar baja</button>
                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setMostrarBaja(false)}>Cancelar</button>
                </div>
            )}
        </>
    );
}

function mapCobroAForm(cobro) {
    return {
        tipoPago: cobro.tipoPago || '',
        cantidadHaberes: cobro.cantidadHaberes ?? '',
        conPorcentaje: !!cobro.conPorcentaje,
        porcentaje: cobro.porcentaje ?? '',
        montoTotal: cobro.montoTotal ?? '',
        banco: cobro.banco || '',
        fechaFavorable: cobro.fechaFavorable || '',
        fechaAlta: cobro.fechaAlta || '',
        observaciones: cobro.observaciones || '',
    };
}

// -------- Pagos --------

/** Anula un pago cargado mal (se borra del cobro y el saldo se recalcula) y avisa cómo salió. */
export async function anularPagoDelCobro(cobro, idPago, onCambio) {
    try {
        await anularPagoCobro(cobro.idCobro, idPago);
        toast.success('Pago anulado.');
        onCambio?.();
    } catch (err) {
        toast.error(err.response?.data?.mensaje || 'No se pudo anular el pago.');
    }
}

// -------- Bitácora --------

/** Alta de una gestión (llamado, WhatsApp, nota…): canal + detalle, con la fecha de hoy. */
export function FormularioGestionCobro({ cobro, onGuardado, className = 'd-flex gap-2 align-items-start mb-4 flex-wrap' }) {
    const [canal, setCanal] = useState('LLAMADA');
    const [detalle, setDetalle] = useState('');
    const [guardando, setGuardando] = useState(false);

    const handleAgregar = async () => {
        if (!detalle.trim()) {
            toast.error('Escribí el detalle de la gestión.');
            return;
        }
        setGuardando(true);
        try {
            await registrarGestionCobro(cobro.idCobro, { fecha: hoyIso(), canal, detalle: detalle.trim() });
            setDetalle('');
            onGuardado?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo registrar la gestión.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div className={className}>
            <select className="form-select" style={{ maxWidth: 180 }} value={canal} onChange={(e) => setCanal(e.target.value)}>
                {CANALES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="text" className="form-control" style={{ flex: 1, minWidth: 220 }} placeholder="Detalle de la gestión..." value={detalle} onChange={(e) => setDetalle(e.target.value)} />
            <button className="btn btn-primary rounded-pill px-4 fw-semibold" onClick={handleAgregar} disabled={guardando}>
                {guardando ? <span className="spinner-border spinner-border-sm" /> : 'Agregar'}
            </button>
        </div>
    );
}

// El recordatorio de cuota (V263) lo asienta el sistema: se muestra con un nombre legible.
const ETIQUETA_CANAL_SISTEMA = { RECORDATORIO: 'Recordatorio WhatsApp' };

/** Las gestiones registradas, la más nueva primero (el backend ya las manda ordenadas). */
export function ListaGestionesCobro({ gestiones = [] }) {
    if (gestiones.length === 0) {
        return <p className="text-muted text-center py-4 mb-0">Sin gestiones registradas.</p>;
    }
    return (
        <div className="d-flex flex-column gap-2">
            {gestiones.map((g, i) => {
                // La más nueva (la primera: el backend ya las manda ordenadas) va resaltada, suave:
                // con muchas gestiones es la que se busca y se perdía entre las demás. Con una sola
                // no hace falta distinguir nada.
                const esUltima = i === 0 && gestiones.length > 1;
                return (
                    <div
                        key={g.id}
                        className="rounded-4 p-3 shadow-sm"
                        data-ultima-gestion={esUltima || undefined}
                        style={esUltima
                            ? { background: '#eff6ff', borderLeft: '5px solid #3b82f6' }
                            : { background: '#f8fafc', borderLeft: '5px solid #94a3b8' }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fw-semibold">
                                {formatoFecha(g.fecha)}
                                {esUltima && (
                                    <span className="ms-2" style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1d4ed8' }}>Última</span>
                                )}
                            </span>
                            {g.canal && (
                                <span
                                    className="badge rounded-pill"
                                    style={g.canal === 'RECORDATORIO'
                                        ? { background: '#dcfce7', color: '#166534' }
                                        : { background: '#e2e8f0', color: '#334155' }}
                                >
                                    {ETIQUETA_CANAL_SISTEMA[g.canal] || g.canal}
                                </span>
                            )}
                        </div>
                        <div>{g.detalle}</div>
                        <small className="text-muted">{g.usuarioNombre || 'Sistema'} · {formatoFechaHora(g.creadoEn)}</small>
                    </div>
                );
            })}
        </div>
    );
}
